import type { ChangeEvent } from "react";
import { useEffect, useMemo, useState } from "react";

import type { ISite } from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import { GeneralSettings } from "app/pages/activation/components/Settings/GeneralSettings/GeneralSettings";
import { CompetitorsSection } from "app/pages/activation/components/Settings/TabsContent/GeneralTab/CompetitorsSection";
import { DeleteSetSection } from "app/pages/activation/components/Settings/TabsContent/GeneralTab/DeleteSetSection";
import { ActivationComponentAssets } from "app/pages/activation/helpers/ActivationAssets";
import { getExistingNames } from "app/pages/activation/helpers/getExistingNames";
import { useCompetitorsActions } from "app/pages/activation/hooks/settings/competitors/useCompetitorsActions";
import useNameInput from "app/pages/activation/hooks/setup/useNameInput";
import { initialDataToSave } from "app/pages/activation/modules/Settings/config";
import { AssetKey, CompetitorActions } from "app/pages/activation/modules/Settings/types";
import useActivationObjectQuery from "app/pages/activation/queries/data/useActivationObjectQuery";
import { useShortActivationSetListQuery } from "app/pages/activation/queries/data/useShortActivationSetListQuery";
import { useIconsQuery } from "app/pages/activation/queries/useIconsQuery";
import useSiteMetaInfo from "app/pages/activation/queries/useSiteMetaInfo";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { ICountryObject } from "app/services/CountryService";

import { generateAutomationName } from "../../../../helpers/generateAutomationName";
import { ViewerModeBanner } from "../../ViewerModeBanner/ViewerModeBanner";
import * as SC from "../styles";

import { GaSection, useGaSectionHandlers } from "./GaSection";
import type { GeneralTabContentSchema } from "./type";

export const GeneralTabContent = ({
    setIsChanged,
    setDataToSave,
    isChanged,
    isViewerMode,
}: GeneralTabContentSchema) => {
    const countriesList = swSettings.components?.Activation?.allowedCountries as ICountryObject[];
    const { domain, key: activationId, country } = proNavigator.getApiParams();
    const isMultiProjects = ActivationComponentAssets.isMultiProjects;

    const {
        data,
        isLoading: isActivationLoading,
        isFetched: isActivationFetched,
    } = useActivationObjectQuery(activationId, true);

    const { isLoading, isFetching, data: siteMeta } = useSiteMetaInfo(domain, country);

    const { data: activationSets } = useShortActivationSetListQuery();

    const {
        data: competitorsWithIcons,
        isFetching: isFetchingCompetitors,
        isLoading: isLoadingCompetitors,
    } = useIconsQuery(data?.competitorsDomains, isActivationFetched && !isActivationLoading);

    const [isGeneralSettingsChanged, setIsGeneralSettingsChanged] = useState(false);
    const handleGeneralSettingsChange = (value: boolean) => {
        setIsGeneralSettingsChanged(value);
        setIsChanged(value);
    };
    const handleGaSettingsChange = () => {
        setIsChanged(true);
    };
    const {
        gaMapping,
        isLoading: isGaMappingLoading,
        ...gaSectionProps
    } = useGaSectionHandlers({
        activationId,
        onChange: handleGaSettingsChange,
    });

    const { competitors, dispatchCompetitors } = useCompetitorsActions();

    const existingNames = useMemo(() => getExistingNames(activationSets), [activationSets]);

    const { onNameChange, nameError, activationSetName, setActivationSetName } = useNameInput(
        existingNames,
        data?.name,
    );

    const [selectedWebsite, setSelectedWebsite] = useState<ISite>(null);
    const [selectedCountry, setSelectedCountry] = useState(+country);

    useEffect(() => {
        if (siteMeta) {
            setSelectedWebsite({
                name: domain,
                image: siteMeta[domain]?.icon,
            });
        }
    }, [domain, siteMeta]);

    useEffect(() => {
        if (!activationSetName && data?.name && !isChanged) {
            setActivationSetName(data?.name);
        }
    }, [data, activationSetName]);

    useEffect(() => {
        if (isFetchingCompetitors || isLoadingCompetitors) {
            return;
        }

        const competitorsSet = competitorsWithIcons
            ? Object.keys(competitorsWithIcons).map((name) => ({
                  name,
                  image: competitorsWithIcons[name],
              }))
            : [];

        if (competitorsSet) {
            dispatchCompetitors({
                type: CompetitorActions.set,
                payload: { asset: competitorsSet, assetKey: AssetKey.competitors },
            });
        }
    }, [isFetchingCompetitors, isLoadingCompetitors]);

    useEffect(() => {
        setDataToSave((dataToSave) => ({
            ...initialDataToSave,
            gaMapping: dataToSave.gaMapping,
            isGeneralSettingsChanged,
            selectedCountry,
            selectedWebsite,
            competitors,
            trafficSource: data?.trafficSource,
            activationSetName,
            nameError,
        }));
    }, [
        activationSetName,
        competitors,
        isGeneralSettingsChanged,
        nameError,
        selectedCountry,
        selectedWebsite,
        setDataToSave,
    ]);

    useEffect(() => {
        setDataToSave((dataToSave) => ({
            ...dataToSave,
            gaMapping,
        }));
    }, [gaMapping, setDataToSave]);

    useEffect(() => {
        setDataToSave((dataToSave) => ({
            ...dataToSave,
            gaMapping,
        }));
    }, [gaMapping, setDataToSave]);

    const onChange = ({ id }: ICountryObject) => {
        setSelectedCountry(id);
        handleGeneralSettingsChange(id !== country);
    };

    const onNameInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (isViewerMode) {
            return;
        }

        const value = e.target.value;
        onNameChange(value);

        handleGeneralSettingsChange(value !== data?.name);
    };

    return (
        <>
            <SC.Section
                data-automation={generateAutomationName("settings", ["section", "general"])}
                className="noTopPadding"
                withSeparator
            >
                <ViewerModeBanner isViewerMode={isViewerMode} />

                <GeneralSettings
                    onNameChange={onNameInputChange}
                    nameInputError={nameError}
                    onChange={onChange}
                    isLoading={isLoading || isFetching}
                    selectedWebsite={selectedWebsite}
                    selectedCountry={selectedCountry}
                    activationSetName={activationSetName}
                    countriesList={countriesList}
                    isViewerMode={isViewerMode}
                    isMultiProjects={isMultiProjects}
                />
            </SC.Section>

            <GaSection
                {...gaSectionProps}
                isLoading={isActivationLoading || isGaMappingLoading}
                gaMapping={gaMapping}
                isViewerMode={isViewerMode}
            />

            <CompetitorsSection
                competitors={competitors}
                isFetchingCompetitors={isFetchingCompetitors}
                dispatchCompetitors={dispatchCompetitors}
                isLoadingCompetitors={isLoadingCompetitors}
                selectedWebsite={selectedWebsite}
                setIsChanged={handleGeneralSettingsChange}
                isViewerMode={isViewerMode}
            />

            <DeleteSetSection
                activationId={activationId}
                isViewerMode={isViewerMode}
                topicsCount={data?.assets?.topics?.length}
            />
        </>
    );
};
